import { segment } from '@actions/analytics';
import Dynamic from '@base/Dynamic/Dynamic';
import { PLANNER_IDB_NOT_REACHABLE } from '@constants/segment';
import isIndexedDBValid from '@cont/Planner/planner-utils/db-handler/indexed-db/isIndexedDBValid';
import {
  enablePlannerDiagnostics,
  updatePlannerDiagnosticsData
} from '@cont/Planner/planner-utils/diagnostics/collector';
import useRequestStatusStore from '@stores/requestStatus';
import { getUberLoaderVisibilityUpdater } from '@stores/selectors/requestStatus';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { isStorageAvailable } from './planner-utils/db-handler/db-handler';
import LoaderSkeleton from './Skeletons/LoaderSkeleton';

const Planner = Dynamic(() => import('./Planner'));
const WAIT_TIME_FOR_IDB = 7 * 1000;

const PlannerContainer = () => {
  const [isDetectingDB, setIsDetectingDB] = useState(true);
  const [showSkeletonLoader, setShowSkeletonLoader] = useState(false);

  const dbDetectionTimer = useRef();
  const loaderTimer = useRef();

  const setShowUberLoader = useRequestStatusStore(
    getUberLoaderVisibilityUpdater
  );

  useEffect(() => {
    isStorageAvailable().then(() => {
      setIsDetectingDB(false);
      enablePlannerDiagnostics();
    });
  }, []);

  useEffect(() => {
    if (isDetectingDB && isIndexedDBValid()) {
      setShowUberLoader(true);

      // Show skeleton loader only when indexedb is unreachable. Else, we just show uber loader.
      loaderTimer.current = setTimeout(() => {
        if (isDetectingDB) {
          setShowUberLoader(false);
          setShowSkeletonLoader(true);
        }
      }, 2000);

      dbDetectionTimer.current = setTimeout(() => {
        if (isDetectingDB) {
          segment.track(PLANNER_IDB_NOT_REACHABLE);
          window.location.reload();
        }
      }, WAIT_TIME_FOR_IDB);
    } else if (
      !isDetectingDB &&
      isIndexedDBValid &&
      dbDetectionTimer?.current
    ) {
      clearTimeout(dbDetectionTimer.current);
      clearTimeout(loaderTimer.current);
      dbDetectionTimer.current = null;
      loaderTimer.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetectingDB]);

  const {
    query: { goalUID }
  } = useRouter();

  useEffect(() => {
    if (goalUID) {
      updatePlannerDiagnosticsData({ goalUID });
    }
  }, [goalUID]);

  if (isDetectingDB && showSkeletonLoader) return <LoaderSkeleton />;

  if (isDetectingDB) return null;
  return <Planner key={goalUID} />;
};

export default PlannerContainer;
